/**
 * Speech-bubble tail shapes as SVG path data, shared by the editor (rendered
 * as <path>) and the page renderer (drawn with Path2D), so a tail looks the
 * same on screen and in every export.
 */

const inwardNormal = (edge) => {
  if (edge === "top") return { x: 0, y: 1 };
  if (edge === "bottom") return { x: 0, y: -1 };
  if (edge === "left") return { x: 1, y: 0 };
  if (edge === "right") return { x: -1, y: 0 };
  return { x: 0, y: 0 };
};

const polygonPath = (points) =>
  `M ${points.map((p) => `${p[0]},${p[1]}`).join(" L ")} Z`;

const polylinePath = (points) =>
  `M ${points.map((p) => `${p[0]},${p[1]}`).join(" L ")}`;

/**
 * Geometry for one tail, in bubble-local px.
 *
 * - cover: filled (no stroke) strip that erases the bubble's border where
 *   the tail joins it
 * - fill: the tail body, filled with the bubble colour
 * - stroke: the tail's outer edges only (never the base on the bubble)
 * - circles: thought-bubble dots instead of a body
 * - lineJoin: how the outer edges meet
 */
export const tailShape = (triangle, style, strokeWidth) => {
  const b1 = triangle.base1;
  const b2 = triangle.base2;
  const tip = triangle.tip;
  const n = inwardNormal(triangle.edge);
  const inset = strokeWidth + 1;
  const cover = polygonPath([
    [b1.x, b1.y], [b2.x, b2.y],
    [b2.x + n.x * inset, b2.y + n.y * inset], [b1.x + n.x * inset, b1.y + n.y * inset],
  ]);

  if (style === "thought") {
    const bx = (b1.x + b2.x) / 2;
    const by = (b1.y + b2.y) / 2;
    const count = 3;
    const circles = [];
    for (let i = 0; i < count; i++) {
      const t = (i + 1) / (count + 1);
      circles.push({ cx: bx + (tip.x - bx) * t, cy: by + (tip.y - by) * t, r: Math.max(3, 8 - i * 2) });
    }
    return { cover: null, fill: null, stroke: null, circles, lineJoin: "miter" };
  }

  if (style === "jagged") {
    const midX = (b1.x + b2.x) / 2;
    const midY = (b1.y + b2.y) / 2;
    const dx = tip.x - midX;
    const dy = tip.y - midY;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = (-dy / len) * 6;
    const py = (dx / len) * 6;
    const p1x = midX + dx * 0.33 + px;
    const p1y = midY + dy * 0.33 + py;
    const p2x = midX + dx * 0.66 - px;
    const p2y = midY + dy * 0.66 - py;
    const outer = [
      [b1.x, b1.y], [p1x, p1y], [p2x, p2y], [tip.x, tip.y],
      [p2x + px * 0.5, p2y + py * 0.5], [p1x - px * 0.5, p1y - py * 0.5], [b2.x, b2.y],
    ];
    return { cover, fill: polygonPath(outer), stroke: polylinePath(outer), circles: [], lineJoin: "bevel" };
  }

  if (style === "curved") {
    const midX = (b1.x + b2.x) / 2;
    const midY = (b1.y + b2.y) / 2;
    const dx = tip.x - midX;
    const dy = tip.y - midY;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const cpx = midX + dx * 0.5 + (-dy / len) * 15;
    const cpy = midY + dy * 0.5 + (dx / len) * 15;
    const cp2x = cpx + (b2.x - b1.x) * 0.1;
    const cp2y = cpy + (b2.y - b1.y) * 0.1;
    const outline = `M ${b1.x},${b1.y} Q ${cpx},${cpy} ${tip.x},${tip.y} Q ${cp2x},${cp2y} ${b2.x},${b2.y}`;
    return { cover, fill: `${outline} Z`, stroke: outline, circles: [], lineJoin: "miter" };
  }

  if (style === "right-left" || style === "right-right") {
    // One leg leaves the edge at a right angle; the other meets the tip
    const anchor = style === "right-left" ? b1 : b2;
    const other = style === "right-left" ? b2 : b1;
    const vertical = triangle.edge === "top" || triangle.edge === "bottom";
    const knee = vertical ? [anchor.x, tip.y] : [tip.x, anchor.y];
    const outer = [[anchor.x, anchor.y], knee, [tip.x, tip.y], [other.x, other.y]];
    return { cover, fill: polygonPath(outer), stroke: polylinePath(outer), circles: [], lineJoin: "miter" };
  }

  // Pointed
  const outer = [[b1.x, b1.y], [tip.x, tip.y], [b2.x, b2.y]];
  return { cover, fill: polygonPath(outer), stroke: polylinePath(outer), circles: [], lineJoin: "miter" };
};

/** The tail style a bubble shape uses when the tail doesn't name one. */
export const defaultTailStyle = (shapeDef) => (shapeDef.tailType === "thought" ? "thought" : "pointed");
